/**
 * Centralised application configuration loaded from Vite env vars.
 *
 * CR017 (Engineering Quality): single audit point for every `VITE_*` value
 * the app actually consumes at runtime. When adding a new variable, update:
 *   1. This file (the runtime reader).
 *   2. `src/vite-env.d.ts` (the type contract).
 *   3. `.env`, `.env.staging`, `.env.production` (deploy values).
 *
 * Why this module exists:
 *   - One place to discover which env vars the runtime actually reads.
 *   - Required vars surface a clear warning in dev when missing, instead of
 *     silently sending requests to `undefined/...`.
 *   - URLs are grouped semantically (api / auth) so call sites can ask for
 *     the right thing by name.
 *
 * Direct access to `import.meta.env.VITE_*` outside this file should be
 * avoided — import from `@/shared/config/env` instead.
 */

type ViteEnvKey = keyof ImportMetaEnv;

const readString = (name: ViteEnvKey): string | undefined => {
  const value = import.meta.env[name];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const required = (name: ViteEnvKey): string => {
  const value = readString(name);
  if (value === undefined) {
    // Warn loudly in dev so the missing-config surface is obvious, but don't
    // throw: tests / Storybook may legitimately boot without a real backend
    // and the network calls will simply fail with a clearer error downstream.
    if (import.meta.env.DEV) {
      console.warn(
        `[env] Missing required environment variable "${name}". ` +
          `Define it in .env / .env.<mode>.`
      );
    }
    return '';
  }
  return value;
};

const optional = (name: ViteEnvKey, fallback: string): string =>
  readString(name) ?? fallback;

/**
 * Default AES key used by `secureStorage` when `VITE_ENCRYPTION_KEY` is not
 * set. Intentionally non-empty so localStorage encryption keeps working in
 * local dev / test environments — production deployments MUST override it.
 */
const DEFAULT_ENCRYPTION_KEY =
  'HelloHealth_Default_Secret_Key_Change_In_Production_Min32Chars';

export const env = {
  api: {
    /**
     * Main portal-service REST base, includes the `/api` suffix.
     * Used as the default `baseURL` for the shared axios instance.
     * Source: `VITE_API_BASE_URL`.
     */
    baseUrl: required('VITE_AUTH_API_BASE_URL')+'/api',
    /**
     * Auth service host (no `/api` suffix, paths are mounted at the root):
     * `/signin`, `/signout`, `/refreshToken`.
     * Source: `VITE_AUTH_API_BASE_URL`.
     */
    authBaseUrl: required('VITE_AUTH_API_BASE_URL'),
  },
  security: {
    /**
     * AES key for `secureStorage` (encrypted localStorage). Falls back to a
     * hard-coded dev-only key so local builds keep working; production
     * deployments MUST set `VITE_ENCRYPTION_KEY`.
     */
    encryptionKey: optional('VITE_ENCRYPTION_KEY', DEFAULT_ENCRYPTION_KEY),
  },
} as const;

export type AppEnv = typeof env;
